import { ChevronRightIcon } from '@heroicons/react/24/outline';
import {
  Edit01Icon,
  PlusIcon,
  Trash01Icon,
} from '@langchain/untitled-ui-icons';
import { Chip } from '@mui/joy';

import { Fragment, useState } from 'react';

import { PlaygroundButton } from '@/Pages/Playground/PlaygroundHome/PlaygroundButtons';
import {
  convertStructuredPromptSchemaToTool,
  convertToolToStructuredPromptSchema,
} from '@/Pages/Playground/PlaygroundHome/tools/convertTools.utils';
import { StructuredPromptSchema } from '@/Pages/Playground/components/manifest/serialized/prompts/ManifestStructuredPrompt';
import { CodeData } from '@/components/Code/Code';
import Header from '@/components/CodeHeader';
import { CopyButton } from '@/components/CopyButton/CopyButton';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ErrorBoundaryRawFallback } from '@/components/ErrorBoundaryRawFallback';
import { getDefaultTool } from '@/components/ExampleCrudPane/utils';
import { SchemaEditorModalBody } from '@/components/SchemaEditor/SchemaEditorModalBody';
import { SchemaEditorVariant } from '@/components/SchemaEditor/types';
import { Tooltip } from '@/design-system';
import {
  AnthropicTool,
  JsonSchema,
  OpenAIFunction,
  OpenAIResponseTool,
  isOpenAIFunctions,
  isOpenAIResponseTool,
} from '@/utils/ai-tools';
import { getFunctionsUsed, getInvocationParams } from '@/utils/messages';
import { cn } from '@/utils/tailwind';

import { RunOutputSchema } from '../../../types/schema';
import { getRecognizedToolsFromRun } from '../utils/getRecognizedToolsFromRun';
import { RunDisplaySchema } from '../utils/types';

function isJsonSchemaObject(
  value: unknown
): value is { type: 'object'; properties: Record<string, unknown> } {
  if (typeof value !== 'object' || value === null) return false;
  if (!('type' in value)) return false;
  if (value['type'] !== 'object') return false;
  if (!('properties' in value)) return false;
  if (typeof value['properties'] !== 'object' || value['properties'] === null)
    return false;
  return true;
}

function isJsonSchema(
  value: unknown
): value is { type: string; [key: string]: unknown } {
  if (typeof value !== 'object' || value === null) return false;
  if (!('type' in value)) return false;
  if (typeof value['type'] !== 'string') return false;
  return true;
}

const handledTypes = new Set([
  'string',
  'number',
  'object',
  'boolean',
  'integer',
]);

function RunDetailFunctionParameters({ value }: { value: unknown }) {
  if (isJsonSchemaObject(value)) {
    return (
      <div className="flex flex-col gap-4">
        {Object.entries(value.properties).map(([key, value], index) => {
          if (!isJsonSchema(value)) {
            return (
              <div
                key={index}
                className="grid grid-cols-[auto,1fr] overflow-hidden rounded-lg border border-secondary"
              >
                <div className="col-span-2 border-b border-secondary bg-secondary-hover px-4 py-2 font-mono text-sm">
                  {key}
                </div>

                <div className="col-span-2">
                  <CodeData value={value} readOnly />
                </div>
              </div>
            );
          }

          const type = value.type;

          const unhandledObject: object = { ...value };
          delete unhandledObject['type'];
          delete unhandledObject['title'];
          delete unhandledObject['description'];
          delete unhandledObject['properties'];
          delete unhandledObject['enum'];

          return (
            <div
              key={index}
              className="grid grid-cols-[auto,1fr] overflow-hidden rounded-lg border border-secondary"
            >
              <div className="col-span-2 border-b border-secondary bg-secondary-hover px-4 py-2 font-mono text-sm">
                {key}
              </div>

              {handledTypes.has(type) ? (
                <Fragment>
                  <Fragment>
                    <div className="px-4 py-2 text-sm font-semibold">Type</div>
                    <div className="px-4 py-2 text-sm">{value.type}</div>
                  </Fragment>

                  {'title' in value && typeof value.title === 'string' && (
                    <Fragment>
                      <div className="border-t border-tertiary px-4 py-2 text-sm font-semibold">
                        Title
                      </div>
                      <div className="whitespace-pre-wrap border-t border-tertiary px-4 py-2 text-sm">
                        {value.title}
                      </div>
                    </Fragment>
                  )}

                  {'description' in value &&
                    typeof value.description === 'string' && (
                      <Fragment>
                        <div className="border-t border-tertiary px-4 py-2 text-sm font-semibold">
                          Description
                        </div>
                        <div className="whitespace-pre-wrap border-t border-tertiary px-4 py-2 text-sm">
                          {value.description}
                        </div>
                      </Fragment>
                    )}

                  {'enum' in value && Array.isArray(value.enum) && (
                    <Fragment>
                      <div className="border-t border-tertiary px-4 py-2 text-sm font-semibold">
                        Enum
                      </div>
                      <div className="flex flex-wrap gap-1 border-t border-tertiary px-4 py-2 text-sm">
                        {value.enum.map((item, index) => (
                          <span
                            key={index}
                            className="rounded-md border border-secondary bg-secondary-hover px-1 py-0.5 text-sm"
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                    </Fragment>
                  )}

                  {type === 'object' && 'properties' in value && (
                    <Fragment>
                      <div className="border-t border-tertiary px-4 py-2 text-sm font-semibold">
                        Properties
                      </div>
                      <div className="border-t border-tertiary px-4 py-2 text-sm">
                        <CodeData value={value.properties} readOnly />
                      </div>
                    </Fragment>
                  )}

                  {Object.keys(unhandledObject).length > 0 && (
                    <div className="col-span-2 border-t border-secondary">
                      <CodeData value={unhandledObject} readOnly />
                    </div>
                  )}

                  {/* TODO: Other properties found in object */}
                </Fragment>
              ) : (
                <div className="col-span-2">
                  <CodeData value={value} readOnly />
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-secondary">
      <CodeData value={value} readOnly />
    </div>
  );
}

export function RunDetailFunction(props: {
  function:
    | OpenAIFunction
    | (OpenAIResponseTool & {
        server_label?: string;
        tools?: Array<OpenAIResponseTool & { used: boolean }>;
      })
    | AnthropicTool
    | JsonSchema;
  used?: boolean;
  loading?: boolean;
  onEdit?: (
    mFunction: OpenAIFunction | OpenAIResponseTool | AnthropicTool | JsonSchema
  ) => void;
  onRemove?: (
    mFunction: OpenAIFunction | OpenAIResponseTool | AnthropicTool | JsonSchema
  ) => void;
}) {
  const [open, setOpen] = useState(false);

  const handleEditClick = (e: React.MouseEvent) => {
    if (!props.onEdit) return;
    e.stopPropagation();

    props.onEdit(props.function);
  };

  const handleRemoveClick = (e: React.MouseEvent) => {
    if (!props.onRemove) return;
    e.stopPropagation();

    props.onRemove(props.function);
  };

  let functionName = '';
  if ('name' in props.function && props.function.name) {
    functionName = props.function.name;
  } else if (isOpenAIResponseTool(props.function)) {
    functionName = props.function.type;
  } else if ('title' in props.function && props.function.title) {
    functionName = props.function.title;
  }

  if (functionName === 'mcp' && 'server_label' in props.function) {
    return (
      <div className="flex flex-col bg-background">
        <div
          className={cn('flex items-start gap-2 p-4 pr-3')}
          onClick={() => setOpen((open) => !open)}
        >
          <span className="flex flex-col items-start gap-1">
            <span className="flex flex-row gap-2 font-mono">
              {props.function.server_label}
              <Chip
                variant="soft"
                color="neutral"
                size="sm"
                sx={{ borderRadius: '6px', ml: '2px' }}
              >
                MCP Server
              </Chip>
            </span>
          </span>
          <span className="flex-grow" />
          <ChevronRightIcon
            className={cn(
              'my-auto h-4 w-4 transition-transform',
              open && 'rotate-90'
            )}
          />
        </div>
        {open &&
          'tools' in props.function &&
          props.function.tools?.map((tool) => (
            <div className="border-t border-secondary pl-4">
              <RunDetailFunction function={tool} used={tool.used} />
            </div>
          ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-background">
      <div
        className={cn('flex items-start gap-2 p-4 pr-3')}
        onClick={() => setOpen((open) => !open)}
      >
        <span className="flex flex-col items-start gap-1">
          <span className="flex flex-row gap-2 font-mono">{functionName}</span>
          {open && (
            <span className="text-left text-sm text-tertiary">
              {props.function.description}
            </span>
          )}
        </span>
        <span className="flex-grow" />
        {props.used && (
          <Chip
            variant="soft"
            color="primary"
            size="sm"
            sx={{ borderRadius: '6px', mr: '2px' }}
          >
            CALLED
          </Chip>
        )}
        {props.onEdit && (
          <Tooltip title="Edit tool" side="top">
            <button
              type="button"
              onClick={handleEditClick}
              className={cn(
                'flex items-center justify-center rounded-md p-1 transition duration-75 hover:bg-secondary-hover'
              )}
            >
              <Edit01Icon className="h-4 w-4" />
            </button>
          </Tooltip>
        )}
        {props.onRemove && (
          <Tooltip title="Remove tool" side="top">
            <button
              type="button"
              onClick={handleRemoveClick}
              className={cn(
                'flex items-center justify-center rounded-md p-1 transition duration-75 hover:bg-secondary-hover'
              )}
            >
              <Trash01Icon className="h-4 w-4" />
            </button>
          </Tooltip>
        )}
        <CopyButton
          variant="icon"
          copy={JSON.stringify(props.function)}
          copyText={'Copy tool schema'}
        />
        <span
          className={cn(
            'flex items-center justify-center rounded p-1 transition-all hover:bg-secondary-hover active:bg-secondary'
          )}
        >
          <ChevronRightIcon
            className={cn('h-4 w-4 transition-transform', open && 'rotate-90')}
          />
        </span>
      </div>
      {open && (
        <div className="p-4 pt-0">
          <RunDetailFunctionParameters
            value={
              'parameters' in props.function
                ? props.function.parameters
                : 'input_schema' in props.function
                ? props.function.input_schema
                : props.function
            }
          />
        </div>
      )}
    </div>
  );
}

export function RunDetailsFunctionEditorContent(props: {
  // Only support OpenAITool for now for example editing.
  value: OpenAIFunction;
  onSubmit: (value: OpenAIFunction) => void;
  onClose: () => void;
  onRemove: (mFunction: OpenAIFunction) => void;
}) {
  const selectedFunctionJsonSchema = convertToolToStructuredPromptSchema(
    props.value,
    true
  );
  const handleSubmit = (value: StructuredPromptSchema) => {
    const tool = convertStructuredPromptSchemaToTool(value);
    props.onSubmit(tool);
    props.onClose();
  };

  const handleRemove = () => {
    props.onRemove(props.value);
    props.onClose();
  };

  const [localValue, setLocalValue] = useState(selectedFunctionJsonSchema);
  return (
    <div className="p-4">
      <SchemaEditorModalBody
        value={localValue}
        setValue={setLocalValue}
        onSubmit={handleSubmit}
        variant={SchemaEditorVariant.FUNCTIONS}
        readOnly={false}
        onClose={props.onClose}
        title="Edit tool"
        onRemove={handleRemove}
      />
    </div>
  );
}

export function RunDetailsFunctionEditor(props: {
  functions: OpenAIFunction[];
  onChange: (value: OpenAIFunction[]) => void;
  readOnly: boolean;
  functionsUsed: string[];
}) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const handleRemoveFunction = (fn: OpenAIFunction) => {
    const updatedFunctions = props.functions.filter((f) => f !== fn);
    props.onChange(updatedFunctions);
  };

  return (
    <>
      <div className="flex flex-col divide-y divide-secondary overflow-hidden rounded-lg border border-secondary">
        {props.functions.map((fn, idx) =>
          selectedIndex === idx ? (
            <RunDetailsFunctionEditorContent
              key={idx}
              value={fn}
              onSubmit={(updatedFunction) => {
                const updatedFunctions = [...props.functions];
                updatedFunctions[idx] = updatedFunction;
                props.onChange(updatedFunctions);
              }}
              onClose={() => setSelectedIndex(null)}
              onRemove={() => handleRemoveFunction(fn)}
            />
          ) : (
            <RunDetailFunction
              key={idx}
              function={fn}
              used={Boolean(fn.name && props.functionsUsed.includes(fn.name))}
              onEdit={() => {
                setSelectedIndex(idx);
              }}
              onRemove={() => handleRemoveFunction(fn)}
            />
          )
        )}
        {props.functions.length === 0 && (
          <div className="p-4 text-sm text-tertiary">No tools present.</div>
        )}
      </div>
      <div className="flex gap-2 py-4">
        <PlaygroundButton
          text="Tool"
          onClick={() => {
            props.onChange([...props.functions, getDefaultTool().function]);
          }}
          startDecorator={<PlusIcon className="my-auto h-4 w-4" />}
        />
      </div>
    </>
  );
}

export function RunDetailsFunctionsAndStructuredOutput(props: {
  run: RunDisplaySchema;
  variant?: 'run' | 'plain';
  outputs?: RunOutputSchema;
  onEditFunctions?: (functions: OpenAIFunction[]) => void;
}) {
  const invocationParams = getInvocationParams(props.run);
  return (
    <ErrorBoundary
      fallback={(error) => (
        <div className="border-b border-b-secondary bg-secondary p-4">
          <Header text="Tools" className="pl-3" />
          <ErrorBoundaryRawFallback
            msg={`Error parsing tools. Displaying raw data instead: ${error.message}`}
            data={invocationParams}
          />
        </div>
      )}
    >
      <RunDetailsFunctionsAndStructuredOutputContent {...props} />
    </ErrorBoundary>
  );
}

export function RunDetailsFunctionsAndStructuredOutputContent(props: {
  run: RunDisplaySchema;
  variant?: 'run' | 'plain';
  outputs?: RunOutputSchema;
  onEditFunctions?: (functions: OpenAIFunction[]) => void;
}) {
  const recognizedFunctions = getRecognizedToolsFromRun(props.run);
  const structuredOutput =
    props.run.extra?.options?.ls_structured_output_format?.schema;
  if ((recognizedFunctions || structuredOutput) == null) return null;

  const functionsUsed = props.outputs ? getFunctionsUsed(props.outputs) : [];

  let content;
  if (props.onEditFunctions && isOpenAIFunctions(recognizedFunctions)) {
    content = (
      <RunDetailsFunctionEditor
        functions={recognizedFunctions}
        onChange={props.onEditFunctions}
        readOnly={false}
        functionsUsed={functionsUsed}
      />
    );
  } else {
    content = (
      <>
        {props.onEditFunctions && (
          <div className="p-4 text-sm text-warning">
            Editing tools in simple editor is only supported for OpenAI format.
            View raw to edit other tools.
          </div>
        )}
        {recognizedFunctions != null &&
          recognizedFunctions.map((fn, idx) => (
            <RunDetailFunction
              key={idx}
              function={fn.function ?? fn}
              used={functionsUsed.includes(
                fn.function?.name ?? fn.name ?? fn.type
              )}
            />
          ))}
        {recognizedFunctions == null && structuredOutput != null && (
          <RunDetailFunction function={structuredOutput} used={true} />
        )}
      </>
    );
  }

  if (props.variant === 'plain') return content;

  return (
    <div className="border-b border-b-secondary bg-secondary p-4">
      <Header
        text={recognizedFunctions != null ? 'Tools' : 'Output Schema'}
        className="pl-3"
      />
      <div className="flex flex-col divide-y divide-secondary overflow-hidden rounded-lg border border-secondary">
        {content}
      </div>
    </div>
  );
}
